import React, { useContext } from 'react';

import axios from "axios";
import store from 'store';
import { message } from 'antd';

import AppContext           from '../../context/Context';
import varexeAPIResponseHandler from './varexeAPIResponseHandler';
import * as apiLogStore from './apiLogStore';

/**
 * Configuration: Default behavior for automatic error handling in API interceptor
 * 
 * Set to true: Errors are NOT automatically displayed (components handle manually)
 * Set to false: Errors ARE automatically displayed by the interceptor
 * 
 * TODO: Once all components are updated to use varexeAPIResponseHandler, change this to false
 */
const DEFAULT_SKIP_AUTO_ERROR_HANDLING = true;

export const getHeader = () => {



   const AccessToken = store.get('AccessToken');    


   const header = {
     'Client-Service': 'frontend-client',
     'Auth-Key'      : 'varexe-api',
     'Content-Type'  : 'application/json',
     'Developer-Mode': 'false',
     ...(AccessToken && { Authorization: `Bearer ${AccessToken}` })
   };

   return header;
 }



class Api {


    constructor() {

        this.baseURL = process.env.REACT_APP_API_URL;
        this.isRefreshing = true;
        this.failedRequests = [];
        this.api = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            headers: getHeader(),
        });

        this.beforeRequest    = this.beforeRequest.bind(this);
        this.onRequestFailure = this.onRequestFailure.bind(this);
        this.processQueue     = this.processQueue.bind(this);

        // Request counter and loading handler references
        this.pendingRequests = 0;
        this.hideLoading = null;
        this.loadingTimer = null;

        // Add request interceptor
        this.api.interceptors.request.use(
            (config) => {
                // Avoid CORS preflight 301: never use path/?query (strip trailing slash when params are present)
                if (config.url && config.url.endsWith('/') && config.params && Object.keys(config.params).length > 0) {
                    config.url = config.url.replace(/\/$/, '');
                }
                // Skip auth header for login endpoints
                if (config.url.includes('/auth/login')) {
                    // Remove Authorization header for login and check requests
                    store.set('developerMode', false);
                    delete config.headers.Authorization;
                }

                // If data is FormData, remove Content-Type to let browser set it with boundary
                if (config.data instanceof FormData) {
                    delete config.headers['Content-Type'];
                }

                let developerMode = store.get('developerMode') || false;
                config.headers['Developer-Mode'] = developerMode ? 'true' : 'false';
                
                // Check if this request should skip auto loading
                const skipAutoLoading = config?.skipAutoLoading === true;
                
                if (!skipAutoLoading) {
                    this.pendingRequests++;
                    
                    // Show loading only if it's not already showing
                    if (this.pendingRequests === 1) {
                        if (this.loadingTimer) clearTimeout(this.loadingTimer);
                        
                        this.loadingTimer = setTimeout(() => {
                            if (this.pendingRequests > 0) {
                                this.hideLoading = message.loading('Loading...', 0);
                            }
                        }, 300);
                    }
                }

                // Developer panel: in-memory API log (only when user has started recording)
                if (apiLogStore.getIsRecording()) {
                    const logId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
                    config._requestLogId = logId;
                    const fullUrl = config.baseURL ? `${config.baseURL.replace(/\/$/, '')}${config.url}` : config.url;
                    apiLogStore.addEntry({
                        id: logId,
                        timestamp: new Date().toISOString(),
                        method: (config.method || 'get').toUpperCase(),
                        url: fullUrl,
                        payload: { params: config.params || undefined, data: config.data },
                        status: 'pending',
                    });
                }
                
                return config;
            },
            (error) => {
                this.handleRequestComplete(error.config);
                return Promise.reject(error);
            }
        );

        // Add response interceptor
        this.api.interceptors.response.use(
            (response) => {
                const id = response.config?._requestLogId;
                if (id) {
                    apiLogStore.updateEntry(id, {
                        status: 'fulfilled',
                        statusCode: response.status,
                        response: response.data,
                    });
                }
                this.handleRequestComplete(response.config);
                return response;
            },
            (error) => {
                const id = error.config?._requestLogId;
                if (id) {
                    apiLogStore.updateEntry(id, {
                        status: 'rejected',
                        statusCode: error.response?.status,
                        error: {
                            message: error.message,
                            data: error.response?.data,
                        },
                    });
                }
                this.handleRequestComplete(error.config);
                return this.onRequestFailure(error);
            }
        );

        // Add method to update headers
        this.updateHeaders = this.updateHeaders.bind(this);
    }

    updateHeaders(token) {
        this.api.defaults.headers = {
            ...this.api.defaults.headers,
            'Authorization': token ? `Bearer ${token}` : undefined
        };
    }

    beforeRequest(request) {
        // console.log('beforeRequest');
        request.headers.Authorization    = `Bearer ${store.get('AccessToken')}`;
        return request;
    }

    static onRequestSuccess(response) {
      
        return response;
    }

    async onRequestFailure(err) {
        const { config } = err;
        
        // Check if component explicitly wants to skip auto error handling
        // If not specified, use the default configuration
        const skipAutoErrorHandling = config?.skipAutoErrorHandling !== undefined 
            ? config.skipAutoErrorHandling === true 
            : DEFAULT_SKIP_AUTO_ERROR_HANDLING;

        // Use centralized error handler
        const errorResult = varexeAPIResponseHandler.handleError(err, { skipAutoHandling: skipAutoErrorHandling });

        // Handle specific cases that need special treatment
        // Note: license, not_found, and unauthorized errors are already displayed by varexeAPIResponseHandler
        // regardless of skipAutoErrorHandling setting
        
        if (errorResult.type === 'license' || errorResult.type === 'not_found') {
            // Error already displayed, just return the response
            return Promise.resolve(err.response);
        }

        // Handle unauthorized with redirect
        if (errorResult.type === 'unauthorized' && errorResult.redirect) {
            // If redirect is provided, don't show error (redirect takes precedence)
            if (config.url !== '/auth/check') {
                store.remove('AccessToken');
                store.remove('auth');
                window.location.href = errorResult.redirect;
                return;
            }
            return Promise.resolve(err.response);
        }
        
        // For unauthorized without redirect (e.g. expired Sanctum token returning standard 401),
        // clear the stale session and force the user back to the login page.
        if (errorResult.type === 'unauthorized') {
            if (config.url !== '/auth/check' && config.url !== '/auth/login') {
                store.remove('AccessToken');
                store.remove('auth');
                window.location.href = '/auth/login';
                return;
            }
            return Promise.resolve(err.response);
        }

        // For validation errors and other errors, the error handler already displayed the message
        // Return the response so components can still handle it if needed
        if (errorResult.type === 'validation') {
            // Return response so components can access error details
            return Promise.resolve(err.response);
        }

        // For other errors, return the response
        // Components can check response.data for error details
        return Promise.resolve(err.response);
    }

    processQueue(error, token = null) {
        this.failedRequests.forEach((prom) => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });
        this.failedRequests = [];
    }

    // Helper method to handle request completion
    handleRequestComplete = (config) => {
        // Only decrement if this request was tracking loading
        const skipAutoLoading = config?.skipAutoLoading === true;
        
        if (!skipAutoLoading) {
            this.pendingRequests--;
        }
        
        // Clear timer if request completes before loading is shown
        if (this.loadingTimer) {
            clearTimeout(this.loadingTimer);
            this.loadingTimer = null;
        }

        // Hide loading only when all requests are complete
        if (this.pendingRequests === 0) {
            if (this.hideLoading) {
                this.hideLoading();
                this.hideLoading = null;
            }
        }
    }
}

const request = new Api();

// Export both the axios instance and the Api class instance
export { request, varexeAPIResponseHandler };
export default request.api;

/**
 * Usage Examples:
 * 
 * CURRENT DEFAULT: skipAutoErrorHandling = true (errors NOT automatically displayed)
 * 
 * 1. Default behavior (errors NOT automatically displayed - handle manually):
 *    api.post('/endpoint', data)
 *      .then(response => { ... })
 *      .catch(error => {
 *        // Handle error manually
 *        varexeAPIResponseHandler.handleError(error, { skipAutoHandling: false });
 *        // or use varexeAPIResponseHandler.error('Custom message');
 *      });
 * 
 * 2. Enable automatic error handling for specific request:
 *    api.post('/endpoint', data, { skipAutoErrorHandling: false })
 *      .then(response => { ... })
 *      .catch(error => { ... }); // Error message automatically displayed
 * 
 * 3. Use varexeAPIResponseHandler directly for success/error messages:
 *    import { varexeAPIResponseHandler } from 'services/api';
 *    varexeAPIResponseHandler.success('Operation completed!');
 *    varexeAPIResponseHandler.error('Something went wrong');
 * 
 * TO CHANGE DEFAULT: Update DEFAULT_SKIP_AUTO_ERROR_HANDLING constant above
 */